/* eslint-disable react/jsx-boolean-value */
import * as uuid from "uuid";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import AdGroupsDashboard from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/AdGroupsDashboard";
import Button from "../../../../../components/elements/Button";
import Loader from "../../../../../components/elements/Loader";
import api from "../../../../../api";
import classes from "./UploadCreativesModal.module.scss";
import { AdGroup, AdGroupType, CreativeAsset } from "../../../../../types/adGroupTypes";
import { CreativeIdsToDelete } from "../../../../../types/creativeTypes";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { createAdGroup, uploadCreativeAssets } from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";

type UploadCreativeModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  beeswaxAdvertiserId: number;
  brandgenAdvertiserId: string;
  landingPage: string;
  fetchedAdGroupId: string;
  fetchAndSetAdGroup: () => Promise<void>;
  brandgenCampaignId: string;
  fetchedCreativesAssets: CreativeAsset[];
};

const getCampaignById = async (brandgenCampaignId: string) => {
  try {
    return await api.getCampaignById(brandgenCampaignId);
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const UploadFromTemplateModal: FC<UploadCreativeModalProps> = ({
  isOpen,
  handleClose,
  beeswaxAdvertiserId,
  brandgenAdvertiserId,
  landingPage,
  fetchedAdGroupId,
  fetchAndSetAdGroup,
  brandgenCampaignId,
  fetchedCreativesAssets,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isVideoCampaign, setIsVideoCampaign] = useState<boolean>(false);

  const user = useSelector((state: ReduxApplicationState) => state.user);
  const adGroupToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupToCreate);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser)!;
  const adGroupTemplates = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupTemplates);

  const handleUpload = async () => {
    const { name, description, creativeAssets } = adGroupToCreate;
    try {
      if (creativeAssets.length !== 0 && !isDisabled) {
        setIsLoading(true);
        setIsDisabled(true);
        const adGroupType = creativeAssets[0].fileType as AdGroupType;
        const adGroupToCreateBody = {
          adGroupId: `bg-ad-group-${uuid.v4()}`,
          brandgenAdvertiserId: brandgenAdvertiserId!,
          brandgenCampaignId,
          createdBy: user.currentUser,
          name,
          adGroupType,
          description: description ?? "",
        };
        const createdAdGroup: AdGroup = await createAdGroup(adGroupToCreateBody);
        if (createdAdGroup) {
          try {
            await uploadCreativeAssets(
              creativeAssets,
              beeswaxAdvertiserId.toString(),
              brandgenAdvertiserId,
              landingPage,
              createdAdGroup.adGroupId,
              api.createCreative,
              brandgenCampaignId,
            );
          } catch (error) {
            console.error("Error uploading creatives or creating templates", error);
            throw error;
          }

          // Deletion
          await api.deleteAdGroup(fetchedAdGroupId);

          const unresolvedPromises = fetchedCreativesAssets.map((fetchedCreativeAsset) => {
            const creativeIdsToDelete: CreativeIdsToDelete = {
              bgCreativeId: fetchedCreativeAsset.bgCreativeId,
              beeswaxCreativeId: fetchedCreativeAsset.beeswaxCreativeId,
            };
            return api.deleteCreative(creativeIdsToDelete);
          });
          await Promise.all(unresolvedPromises);
        }
        await fetchAndSetAdGroup();
        setIsLoading(false);
        setIsDisabled(false);
        handleClose();
        successToaster("Creatives successfully replaced.", 2000);
      } else {
        errorToaster("Please, select Ad Group to continue.", 5000);
      }
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      setIsDisabled(false);
      errorToaster(`There was an error: ${err.message}`);
    }
  };

  useEffect(() => {
    (async () => {
      const { payload: campaign } = await getCampaignById(brandgenCampaignId);
      setIsVideoCampaign(campaign.isVideoCampaign);
    })();
  }, [adGroupTemplates, brandgenCampaignId]);

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Segment Creation Modal"
      className={classes.container}
      maxWidth="lg"
    >
      <div className={classes.dialogTitle}>
        <Typography variant="h2">Choose creatives from templates</Typography>
      </div>
      {isLoading ? (
        <div className={classes.loader}>
          <Loader isAbsolutePosition={false} size={36} />
        </div>
      ) : (
        <DialogContent className={classes.dialogContent} style={{ display: "flex", flexDirection: "column" }}>
          <AdGroupsDashboard
            selectedAdvertiser={selectedAdvertiser}
            showCheckbox={true}
            isVideoCampaign={isVideoCampaign}
          />
          <Button
            variant="outlined"
            disabled={isDisabled}
            onClick={handleUpload}
            style={{
              marginTop: 10,
              marginLeft: "auto",
              alignSelf: "flex-end",
              maxWidth: 150,
            }}
          >
            Choose
          </Button>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UploadFromTemplateModal;
